import { useMutation, useQueryClient } from '@tanstack/react-query';
import { catalogueStatusSchema, compatibilityDefinitionInputSchema, type CatalogueStatus, type CompatibilityConfig } from 'fuel-benchmark-contracts';
import { useEffect, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { FormField } from '../../components/ui/FormField';
import { Select, type SelectOption } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { Textarea } from '../../components/ui/Textarea';
import { serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { toDefinitionJson } from './condition-display';

export interface CompatibilitySheetProps {
  isOpen: boolean;
  onClose: () => void;
  config: CompatibilityConfig | null;
}

/**
 * The comparison-eligibility half of the Conditions module. Whether a mismatch blocks a comparison or merely
 * warns is a value in this JSON (`onFail`), which is exactly why the temperature spread could be downgraded to a
 * warning (plan §12.3) without touching code.
 */
export const CompatibilitySheet = ({ isOpen, onClose, config }: CompatibilitySheetProps) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();

  const [definitionText, setDefinitionText] = useState('');
  const [status, setStatus] = useState<CatalogueStatus>('ACTIVE');
  const [definitionError, setDefinitionError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const updateCompatibility = useMutation(orpc.config.compatibility.update.mutationOptions());

  useEffect(() => {
    if (!isOpen || !config) {
      return;
    }

    setDefinitionText(toDefinitionJson(config.definition));
    setStatus(config.status);
    setDefinitionError(null);
    setSubmitError(null);
  }, [isOpen, config]);

  const onSave = async (): Promise<void> => {
    if (!config) {
      return;
    }

    setDefinitionError(null);
    setSubmitError(null);

    let parsedJson: unknown;

    try {
      parsedJson = JSON.parse(definitionText);
    } catch {
      setDefinitionError(t('conditions.rules.form.invalidJson'));

      return;
    }

    const definition = compatibilityDefinitionInputSchema.safeParse(parsedJson);

    if (!definition.success) {
      setDefinitionError(definition.error.issues.map((issue) => `${issue.path.join('.') || 'definition'}: ${issue.message}`).join(' · '));

      return;
    }

    try {
      await updateCompatibility.mutateAsync({ id: config.id, definition: definition.data, status });
      await queryClient.invalidateQueries({ queryKey: orpc.config.compatibility.list.key() });
      onClose();
    } catch (error) {
      setSubmitError(serverMessage(error) ?? t(submitErrorKey(error)));
    }
  };

  const statusOptions: SelectOption[] = catalogueStatusSchema.options.map((value) => ({ value, label: t(`enums.status.${value}`) }));

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={config ? config.key : t('conditions.compatibility.form.title')}
      description={t('conditions.compatibility.form.subtitle')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            isLoading={updateCompatibility.isPending}
            onClick={() => void onSave()}
          >
            {t('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField
          id="compatibility-protocol"
          label={t('conditions.rules.columns.protocol')}
        >
          <p className="text-sm text-muted-foreground">{config ? `${config.protocolCode} v${config.protocolVersion}` : t('common.notSet')}</p>
        </FormField>

        <FormField
          id="compatibility-definition"
          label={t('conditions.compatibility.form.definition')}
          error={definitionError ?? undefined}
          hint={t('conditions.compatibility.form.definitionHint')}
        >
          <Textarea
            id="compatibility-definition"
            className="font-mono text-xs"
            rows={10}
            spellCheck={false}
            value={definitionText}
            hasError={definitionError !== null}
            onChange={(event) => setDefinitionText(event.target.value)}
          />
        </FormField>

        <FormField
          id="compatibility-status"
          label={t('conditions.rules.columns.status')}
        >
          <Select
            id="compatibility-status"
            options={statusOptions}
            value={status}
            onChange={(event) => setStatus(event.target.value as CatalogueStatus)}
          />
        </FormField>

        {submitError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {submitError}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
};
